import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import api from "api/axiosConfig";
import API_BASE from "configs/hostConfig";
import {
  Box,
  Flex,
  Text,
  Icon,
  Heading,
  Avatar,
  SimpleGrid,
  HStack,
  VStack,
  useBreakpointValue,
} from "@chakra-ui/react";
import { WarningTwoIcon } from "@chakra-ui/icons";
import { pulseRing } from "./StudentProfile.styles";
import { FaLeaf } from "react-icons/fa";
import { LuFlower } from "react-icons/lu";
import { GiAppleSeeds } from "react-icons/gi";
import ProgressPie from "./components/ProgressPie";
import MonthBarChart from "./components/MonthBarChart";
import ScoreLineChart from "./components/ScoreLineChart";
import CategoryPie from "./components/CategoryPie";
import CategoryRadar from "./components/CategoryRadar";
import avatar_img from "assets/user.png";

// import { report_data } from './data/report_data';

const StudentReportPage = () => {
  const navigate = useNavigate();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const isTablet = useBreakpointValue({ md: true, lg: false });

  const [username, setUsername] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [rewards, setRewards] = useState({});
  const [essayProgress, setEssayProgress] = useState({});
  const [storyProgress, setStoryProgress] = useState({});
  const [essayProgressRecords, setEssayProgressRecords] = useState({});
  const [storyProgressRecords, setStoryProgressRecords] = useState({});
  const [intervalCounts, setIntervalCounts] = useState([]);
  const [intervalScores, setIntervalScores] = useState([]);
  const [primaryTypeCounts, setPrimaryTypeCounts] = useState([]);
  const [primaryTypeScores, setPrimaryTypeScores] = useState([]);
  const [minScore, setMinScore] = useState(0);
  const [maxScore, setMaxScore] = useState(100);
  const [minTypeScore, setMinTypeScore] = useState(0);
  const [maxTypeScore, setMaxTypeScore] = useState(100);

  // useEffect(() => {
  //   setUsername(report_data?.username || 'Dynivy User');
  //   setAvatarUrl(report_data?.avatarUrl);
  //   setRewards(report_data?.rewards || {});
  //   setEssayProgress(report_data?.essayProgress);
  //   setStoryProgress(report_data.storyProgress);
  //   setEssayProgressRecords(report_data?.essayProgress?.records || []);
  //   setStoryProgressRecords(report_data?.storyProgress?.records || []);
  //   setIntervalCounts(report_data.completedItemCounts);
  //   setIntervalScores(report_data.intervalScores);
  //   setPrimaryTypeCounts(report_data.primaryTypeCounts);
  //   setPrimaryTypeScores(report_data.primaryTypeScores);
  //   setMinScore(report_data.minScore);
  //   setMaxScore(report_data.maxScore);
  //   setMinTypeScore(report_data.minTypeScore);
  //   setMaxTypeScore(report_data.maxTypeScore);
  // }, []);

  useEffect(() => {
    api
      .get(`${API_BASE}/api/reports`)
      .then((res) => {
        if (res.status == 200) {
          const report_data = res.data?.data;
          // console.log('reports data: ', report_data);
          setUsername(report_data?.username || "Dynivy User");
          setAvatarUrl(report_data?.avatarUrl);
          setRewards(report_data?.rewards || {});
          setEssayProgress(report_data?.essayProgress);
          setStoryProgress(report_data.storyProgress);
          setEssayProgressRecords(report_data?.essayProgress?.records || []);
          setStoryProgressRecords(report_data?.storyProgress?.records || []);
          setIntervalCounts(report_data.completedItemCounts);
          setIntervalScores(report_data.intervalScores);
          setPrimaryTypeCounts(report_data.primaryTypeCounts);
          setPrimaryTypeScores(report_data.primaryTypeScores);
          setMinScore(report_data.minScore);
          setMaxScore(report_data.maxScore);
          setMinTypeScore(report_data.minTypeScore);
          setMaxTypeScore(report_data.maxTypeScore);
        }
      })
      .catch(() => console.log("failed to fetch reports "));
  }, []);

  const NoEnoughDataWarning = ({ msg }) => (
    <Box
      borderRadius="md"
      overflow="hidden"
      boxShadow="md"
      borderWidth="1px"
      textAlign="center"
      p={6}
      my={4}
      maxW="380px"
      alignSelf="center"
    >
      <WarningTwoIcon boxSize={"50px"} color={"orange.300"} />
      <Heading as="h2" size="md" mt={6} mb={2}>
        No Enough Record!
      </Heading>
      <Text color={"gray.500"}>{msg}</Text>
    </Box>
  );

  NoEnoughDataWarning.propTypes = {
    msg: PropTypes.string,
  };

  const profileOverview = () => (
    <VStack align={isMobile || isTablet ? "center" : "start"}>
      <Flex direction="row" alignItems="center" justifyContent="space">
        <Box
          as="div"
          position="relative"
          w="48px"
          h="48px"
          _before={{
            content: "''",
            position: "relative",
            display: "block",
            width: "300%",
            height: "300%",
            boxSizing: "border-box",
            marginLeft: "-100%",
            marginTop: "-100%",
            borderRadius: "50%",
            bgColor: "green",
            animation: `2.25s ${pulseRing} cubic-bezier(0.455, 0.03, 0.515, 0.955) -0.4s infinite`,
          }}
          cursor="pointer"
          onClick={() => navigate("/settings")}
        >
          <Avatar
            src={avatarUrl || avatar_img}
            // src="https://i.pravatar.cc/300"
            size="full"
            position="absolute"
            top={0}
          />
        </Box>

        <Box ml={4}>
          <Heading size="md">{username}</Heading>
        </Box>
      </Flex>
      <HStack align="center" mt={4}>
        <Box bg="green" color="white" borderRadius="md" px={1} mr={4}>
          {rewards["total"]}
        </Box>
        <Icon as={FaLeaf} color="green.500" ml={2} w={5} h={5} />
        <Text size="lg">{rewards["leaf"]}</Text>
        <Icon as={LuFlower} color="purple.500" ml={2} w={5} h={5} />
        <Text size="lg">{rewards["flower"]}</Text>
        <Icon as={GiAppleSeeds} color="orange.600" w={5} h={5} />
        <Text size="lg">{rewards["seed"]}</Text>
      </HStack>
    </VStack>
  );

  const essayProgressOverview = () => (
    <HStack>
      <ProgressPie
        data={essayProgressRecords}
        percent={essayProgress?.completedPercent}
      />
      <VStack ml={{ base: 8, md: 4 }} align="start">
        <Heading size="sm">Essay Writing</Heading>
        <Text>Goal: {essayProgress?.goal}</Text>
        <Text>Completed: {essayProgress?.completed}</Text>
        <Text>In Progress: {essayProgress?.working}</Text>
      </VStack>
    </HStack>
  );

  const storyProgressOverView = () => (
    <HStack ml={{ base: 5, md: 0 }}>
      <ProgressPie
        data={storyProgressRecords}
        percent={storyProgress?.completedPercent}
      />
      <VStack ml={{ base: 8, md: 4 }} align="start">
        <Heading size="sm">Impromptu Story</Heading>
        <Text>Goal: {storyProgress?.goal}</Text>
        <Text>Completed: {storyProgress?.completed}</Text>
        <Text>In Progress: {storyProgress?.working}</Text>
      </VStack>
    </HStack>
  );

  return (
    <Flex flexDirection="column">
      {isMobile || isTablet ? (
        <Flex flexDirection="column">
          {profileOverview()}
          <SimpleGrid
            columns={{ base: 1, md: 2 }}
            mt={{ base: 12, md: 12, lg: 0 }}
            spacing={6}
            placeItems={"center"}
            // ml={{ base: 128, md: 0 }}
          >
            {essayProgressOverview()}
            {storyProgressOverView()}
          </SimpleGrid>
        </Flex>
      ) : (
        <SimpleGrid columns={3} spacing={6} placeItems={"center"} mr={4}>
          {profileOverview()}
          <Flex>{essayProgressOverview()}</Flex>
          <Flex>{storyProgressOverView()}</Flex>
        </SimpleGrid>
      )}
      <SimpleGrid mt={12} columns={{ base: 1, md: 1, lg: 2 }} spacing={6}>
        <VStack>
          <Heading size="sm">
            Creative Output by Month: Essays and Stories
          </Heading>
          <MonthBarChart
            data={intervalCounts}
            graphWidth={isTablet ? 600 : 480}
          />
        </VStack>
        <VStack mt={{ base: 6, md: 6, lg: 0 }}>
          <Heading size="sm">Essay Writing Diversity: Type Breakdown</Heading>
          {primaryTypeCounts?.length > 0 ? (
            <CategoryPie data={primaryTypeCounts} />
          ) : (
            <NoEnoughDataWarning msg="Please finish at least one essay to unlock the chart!" />
          )}
        </VStack>
      </SimpleGrid>
      <SimpleGrid mt={12} columns={{ base: 1, md: 1, lg: 2 }} spacing={6}>
        <VStack>
          <Heading size="sm">
            Writing Excellence: Monthly Essay Score Averages
          </Heading>
          {intervalScores?.length > 1 ? (
            <ScoreLineChart
              data={intervalScores}
              minScore={minScore}
              maxScore={maxScore}
              graphWidth={isTablet ? 600 : 480}
            />
          ) : (
            <NoEnoughDataWarning msg="After two months, you’ll unlock a chart to track your progress." />
          )}
        </VStack>
        <VStack mt={{ base: 6, md: 6, lg: 0 }}>
          <Heading size="sm">
            Essay Writing Strengths: Type-wise Averages
          </Heading>
          {primaryTypeScores?.length > 2 ? (
            <CategoryRadar
              data={primaryTypeScores}
              minScore={minTypeScore}
              maxScore={maxTypeScore}
            />
          ) : (
            <NoEnoughDataWarning msg="Write essays in three different types to unlock your chart—let’s see your versatility shine!" />
          )}
        </VStack>
      </SimpleGrid>
    </Flex>
  );
};

export default StudentReportPage;
